/**
 * Restyles Shopify's built-in policy pages (Contact Information, Shipping,
 * Refund & Return, Terms of Service, Privacy) into a branded card layout.
 *
 * Shopify renders these five legal pages through its own fixed
 * `.shopify-policy__container` markup, completely bypassing theme section
 * files — there is no template hook to change the HTML shape from Liquid.
 * This script re-parses that fixed HTML on every page load (so any edit made
 * in Admin → Settings → Policies flows straight through) and rebuilds it into
 * icon rows inside the existing container. No new page or resource is
 * created; the URL, resource type, and editable text all stay exactly as-is.
 */
(function () {
  var container = document.querySelector('.shopify-policy__container');
  if (!container) return;

  var body = container.querySelector('.shopify-policy__body .rte');
  if (!body) return;

  var ICONS = {
    globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.5 4 5.5 4 9s-1.5 6.5-4 9c-2.5-2.5-4-5.5-4-9s1.5-6.5 4-9z"/>',
    email: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m4 7 8 6 8-6"/>',
    pin: '<path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/>',
    truck: '<rect x="2" y="7" width="12" height="10" rx="1"/><path d="M14 10h4l3 3v4h-7z"/><circle cx="6.5" cy="18.5" r="1.5"/><circle cx="16.5" cy="18.5" r="1.5"/>',
    dollar: '<circle cx="12" cy="12" r="9"/><path d="M12 6.5v11M9.5 15.5c0 1.4 1.2 2 2.7 2s2.8-.7 2.8-2c0-1.6-1.6-2-3-2.3-1.3-.3-2.7-.7-2.7-2.2 0-1.3 1.3-2 2.7-2s2.5.6 2.6 1.8"/>',
    calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
    card: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>',
    box: '<path d="M21 8 12 3 3 8v8l9 5 9-5z"/><path d="M3 8l9 5 9-5M12 13v8"/>',
    shield: '<path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="m9 12 2 2 4-4"/>',
    refresh: '<path d="M21 12a9 9 0 1 1-2.6-6.35M21 4v5h-5"/>',
    ban: '<circle cx="12" cy="12" r="9"/><path d="m6.5 6.5 11 11"/>',
    user: '<circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.5-6 8-6s8 2 8 6"/>',
    share: '<circle cx="6" cy="12" r="2.2"/><circle cx="18" cy="6" r="2.2"/><circle cx="18" cy="18" r="2.2"/><path d="m8 11 8-4M8 13l8 4"/>',
    tag: '<path d="M20.5 12.5 12 21l-9-9V4h8z"/><circle cx="7.5" cy="7.5" r="1.5"/>',
    headset: '<path d="M4 13v-1a8 8 0 0 1 16 0v1"/><rect x="2.5" y="13" width="4" height="6" rx="1.5"/><rect x="17.5" y="13" width="4" height="6" rx="1.5"/><path d="M20 19v1a3 3 0 0 1-3 3h-3"/>',
    instagram: '<rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.2" cy="6.8" r="1"/>',
    tiktok: '<path d="M14 3c.3 1.8 1.5 3.2 3.5 3.5v2.6c-1.3 0-2.5-.4-3.5-1.1v6.4a5 5 0 1 1-5-5c.2 0 .5 0 .7.1v2.7a2.3 2.3 0 1 0 1.7 2.2V3h2.6z"/>',
    doc: '<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4M9 12h6M9 16h6"/>',
    phone: '<path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.9 21 3 13.1 3 3.9c0-.6.4-1 1-1H7.4c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1z"/>',
    whatsapp: '<path d="M12 3.5C7.3 3.5 3.5 6.9 3.5 11c0 2.2 1.1 4.1 2.8 5.5-.1 1-.5 2-1.1 2.9 1.3-.1 2.6-.6 3.6-1.3 1 .3 2.1.5 3.2.5 4.7 0 8.5-3.4 8.5-7.6s-3.8-7.5-8.5-7.5Z"/><path d="M8.7 11h6.6"/>'
  };

  var ICON_RULES = [
    [/website/i, 'globe'],
    [/e-?mail/i, 'email'],
    [/whatsapp/i, 'whatsapp'],
    [/phone|\bmobile\b|\bcall\b/i, 'phone'],
    [/location|service area|address/i, 'pin'],
    [/delivery fee|fee/i, 'dollar'],
    [/estimated|delivery time|business days|tracking/i, 'calendar'],
    [/deliver|shipping/i, 'truck'],
    [/payment|pricing|cash on delivery|cod/i, 'card'],
    [/warranty|rights|how we use/i, 'shield'],
    [/return|replacement|refund|change of mind/i, 'refresh'],
    [/cancel/i, 'ban'],
    [/customer support|contact/i, 'headset'],
    [/information we collect|\buser\b/i, 'user'],
    [/data sharing|share/i, 'share'],
    [/product/i, 'box'],
    [/damaged|defect|arrival/i, 'box']
  ];

  function iconFor(label) {
    for (var i = 0; i < ICON_RULES.length; i++) {
      if (ICON_RULES[i][0].test(label)) return ICONS[ICON_RULES[i][1]];
    }
    return ICONS.doc;
  }

  function svg(paths, extraClass) {
    return (
      '<span class="nue-pol-row-icon' + (extraClass ? ' ' + extraClass : '') + '" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
      paths +
      '</svg></span>'
    );
  }

  function makeRow(label, descHtml, opts) {
    opts = opts || {};
    var isContact = /customer support|contact/i.test(label);
    return (
      '<div class="nue-pol-row' + (isContact ? ' nue-pol-row--contact' : '') + '">' +
      svg(iconFor(label), isContact ? 'nue-pol-row-icon--dark' : '') +
      '<div class="nue-pol-row-body">' +
      '<p class="nue-pol-row-label">' + label + '</p>' +
      '<div class="nue-pol-row-desc">' + descHtml + '</div>' +
      '</div></div>'
    );
  }

  function escapeText(el) {
    return (el.textContent || '').trim();
  }

  var TITLE_RE = /^NUE\s.+(Policy|Service)$/i;

  var DEFAULTS = {
    'shipping-policy': {
      eyebrow: 'SHIPPING POLICY',
      heading: "We're here to get your order to you.",
      subtext: 'This Shipping Policy explains how we deliver your orders, including delivery times, fees and tracking information.'
    },
    'refund-policy': {
      eyebrow: 'REFUND & RETURN POLICY',
      heading: "We're here to help.",
      subtext: 'NUE does not accept returns or refunds for change of mind or preference.'
    },
    'privacy-policy': {
      eyebrow: 'PRIVACY POLICY',
      heading: 'Your privacy matters to us.',
      subtext: 'At NUE, we respect your privacy and are committed to protecting the personal information you share with us.'
    },
    'terms-of-service': {
      eyebrow: 'TERMS OF SERVICE',
      heading: 'Welcome to NUE.',
      subtext: 'By placing an order with NUE, you agree to the following terms.'
    },
    'contact-information': {
      eyebrow: 'CONTACT US',
      heading: 'Have a question?',
      subtext: "Whether you need help with an order, have a question about a product, or simply want to reach out, the NUE team is happy to assist."
    }
  };

  var path = window.location.pathname;
  var key = Object.keys(DEFAULTS).filter(function (k) {
    return path.indexOf(k) !== -1;
  })[0];
  var meta = DEFAULTS[key] || {
    eyebrow: 'POLICY',
    heading: container.querySelector('.shopify-policy__title h1') ? escapeText(container.querySelector('.shopify-policy__title h1')) : 'Policy',
    subtext: ''
  };

  function cleanBlock(node) {
    // Rebuilds the tag with only its own inner content, dropping whatever
    // attributes Shopify's rich-text editor may have left on the <p>/<ul>
    // itself (id/class/style/data-*/lang/dir). Keeps all inner formatting
    // and links untouched.
    var tag = node.tagName.toLowerCase();
    return '<' + tag + '>' + node.innerHTML + '</' + tag + '>';
  }

  function buildStandardRows(nodes) {
    var rows = [];
    var current = null;
    var subtextParts = [];
    var seenLabel = false;

    nodes.forEach(function (node) {
      var tag = node.tagName;
      if (tag !== 'P' && tag !== 'UL' && tag !== 'OL') return;
      var text = escapeText(node);

      if (tag === 'P' && (text === '' || TITLE_RE.test(text))) return;

      var isLabel = tag === 'P' && text.length > 0 && text.length <= 45 && !/[.!?:]$/.test(text);

      if (isLabel) {
        seenLabel = true;
        current = { label: text, html: [] };
        rows.push(current);
        return;
      }

      if (!seenLabel) {
        if (tag === 'P') subtextParts.push(node.innerHTML);
        return;
      }

      if (current) current.html.push(cleanBlock(node));
    });

    if (subtextParts.length) meta.subtext = subtextParts.join(' ');

    return rows
      .filter(function (r) {
        return r.html.length > 0;
      })
      .map(function (r) {
        return makeRow(r.label, r.html.join(''));
      })
      .join('');
  }

  function buildContactRows(nodes) {
    var rowsHtml = '';
    var followHtml = '';
    var noteHtml = '';
    var introParts = [];

    nodes.forEach(function (node) {
      var tag = node.tagName;
      if (tag !== 'P') return;
      var text = escapeText(node);
      if (text === '') return;

      var links = node.querySelectorAll('a[href]');

      if (/^follow nue/i.test(text)) return;

      if (links.length && /instagram\.com|tiktok\.com/i.test(node.innerHTML)) {
        links.forEach(function (a) {
          var href = a.getAttribute('href');
          if (!href || href.indexOf('http') !== 0) return;
          var isInsta = /instagram/i.test(href);
          followHtml +=
            '<a class="nue-pol-social" href="' + href + '" target="_blank" rel="noopener">' +
            svg(isInsta ? ICONS.instagram : ICONS.tiktok) +
            '<span>' + (isInsta ? 'Instagram' : 'TikTok') + '</span></a>';
        });
        return;
      }

      if (/customer support/i.test(text)) {
        var tmp2 = document.createElement('div');
        tmp2.innerHTML = node.innerHTML;
        var lines = (tmp2.innerHTML || '').split(/<br\s*\/?>/i).map(function (l) {
          var d = document.createElement('div');
          d.innerHTML = l;
          return (d.textContent || '').trim();
        }).filter(Boolean);
        rowsHtml += makeRow('Customer Support', '<p>' + lines.slice(1).join('<br>') + '</p>');
        return;
      }

      if (/order-related questions/i.test(text)) {
        noteHtml = text;
        return;
      }

      if (/label\b|:/i.test(node.innerHTML) && node.innerHTML.indexOf(':') !== -1 && node.querySelectorAll('span').length > 2) {
        var raw = node.innerHTML.split(/<br\s*\/?>/i);
        raw.forEach(function (chunk) {
          var tmp = document.createElement('div');
          tmp.innerHTML = chunk;
          var full = (tmp.textContent || '').trim();
          var m = full.match(/^([A-Za-z /]+):\s*(.*)$/);
          if (m) {
            var label = m[1].trim();
            var valueHtml = tmp.querySelector('a') ? tmp.querySelector('a').outerHTML : m[2].trim();
            rowsHtml += makeRow(label, '<p>' + valueHtml + '</p>');
          }
        });
        return;
      }

      // A standalone "Label: value" paragraph (e.g. a single Phone or
      // WhatsApp line added on its own, rather than merged into the
      // combined Website/Email/... line above). Handles the simpler
      // single-span shape the multi-span branch above requires >2 spans
      // for.
      var simpleMatch = text.match(/^([A-Za-z][A-Za-z /]*):\s*(.+)$/);
      if (simpleMatch) {
        var simpleLabel = simpleMatch[1].trim();
        var simpleLink = node.querySelector('a');
        var simpleValueHtml = simpleLink ? simpleLink.outerHTML : simpleMatch[2].trim();
        rowsHtml += makeRow(simpleLabel, '<p>' + simpleValueHtml + '</p>');
        return;
      }

      introParts.push(text);
    });

    return { rowsHtml: rowsHtml, followHtml: followHtml, noteHtml: noteHtml, introParts: introParts };
  }

  var childNodes = Array.prototype.slice.call(body.children);
  var rowsHtml = '';
  var extraHtml = '';

  if (key === 'contact-information') {
    var parsed = buildContactRows(childNodes);
    rowsHtml = parsed.rowsHtml;
    if (parsed.introParts && parsed.introParts.length) {
      var escapedHeading = meta.heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var firstPart = parsed.introParts[0].replace(new RegExp('^\\s*' + escapedHeading + '\\s*', 'i'), '').trim();
      var restParts = parsed.introParts.slice(1);
      meta.subtext = [firstPart].concat(restParts).filter(Boolean).join(' ');
    }
    if (parsed.followHtml) {
      extraHtml =
        '<div class="nue-pol-follow">' +
        '<p class="nue-pol-follow-title">Follow NUE</p>' +
        '<div class="nue-pol-follow-row">' + parsed.followHtml + '</div>' +
        '</div>';
    }
    if (parsed.noteHtml) {
      extraHtml += '<p class="nue-pol-note">' + parsed.noteHtml + '</p>';
    }
  } else {
    rowsHtml = buildStandardRows(childNodes);
  }

  if (!rowsHtml) return;

  var html =
    '<div class="nue-pol-wrap" translate="no">' +
    '<div class="nue-pol-eyebrow"><span class="nue-pol-eyebrow-line"></span><span>' + meta.eyebrow + '</span></div>' +
    '<h1 class="nue-pol-heading">' + meta.heading + '</h1>' +
    (meta.subtext ? '<p class="nue-pol-subtext">' + meta.subtext + '</p>' : '') +
    '<div class="nue-pol-card">' + rowsHtml + '</div>' +
    extraHtml +
    '<p class="nue-pol-signoff">Thanks for being part of NUE.</p>' +
    '</div>';

  container.innerHTML = html;
})();
